import { Injectable } from '@angular/core';
import { Observable, Subscriber, of } from 'rxjs';
import { Product } from '../../order/models/product';
import { OrderService } from './order.service';
import { Order } from '../models/order';
import { OrderInfo } from '../models/order-info';
import { JSONUtilites } from '../../../helpers/JSONUtilites';
import { LoginStateService } from '../../login/services/login-state.service';
import { User } from '../../login/models/user';
import { OrderLine } from '../models/order-line';
import { OrderLineType } from '../models/order-line-type';
import { GiftCardService } from '../../giftcard/services/giftcard.service';
import { GiftCardInMemoryService } from '../../giftcard/services/giftcard-in-memory.service';
import { InMemoryOrderInfos } from '../models/in-memory-order-infos';
import { InMemoryOrders } from '../models/in-memory-orders';
import { TimeRestriction } from '../../booking/models/time-restriction';
import { InMemoryProductPricelists } from '../../payment/models/in-memory-product-pricelists';
import { GlobalSettings } from 'src/environments/global-settings';

@Injectable()
export class OrderInMemoryService implements OrderService {

  private _inMemoryOrders = new InMemoryOrders();
  private _inMemoryProctPricelists = new InMemoryProductPricelists();

  private _orderPrefix = 'DEMO';

  constructor(
    private loginStateService: LoginStateService,
    private giftCardService: GiftCardService,
  ) {
  }

  createOrder(order: Order): Observable<Order> {

    return Observable.create((observer: Subscriber<Order>) => {

      if (this.loginStateService.loggedInUser) {
        const user = this.loginStateService.loggedInUser;
        const orderNumber = this._inMemoryOrders.newItemId;

        const newOrder = new Order(order.orderType, this._inMemoryProctPricelists.items[0]);
        newOrder.name = this._generateOrderName(orderNumber);
        newOrder.id = orderNumber;
        newOrder.bookingDate = GlobalSettings.DateNow;
        if (user) {
          newOrder.partnerId = user.partner_id;
        }
        newOrder.description = order.orderLines && order.orderLines.length ? order.orderLines[0].name : '';
        newOrder.guestOfHonorId = order.guestOfHonorId;

        let pos = 0;
        let lineId = this._inMemoryOrders.newOrderLineId;
        order.orderLines.forEach(line => {

          this._prepareOrderLines(line).forEach(
            orderLineItem => {
              const orderLine = newOrder.insertTypedOrderLine(orderLineItem, pos++);
              orderLine.id = lineId;
              lineId++;
            }
          );
        });

        this._inMemoryOrders.items.push(newOrder);
        this._inMemoryOrders.save();

        observer.next(newOrder);
        observer.complete();
      }
    });
  }

  createGuestOrder(order: Order): Observable<Order> {
    return this.createOrder(order);
  }

  saleToInvoice(orderId: number): Observable<boolean> {
    return of(true);
  }

  getOrder(orderId: number): Observable<Order> {
    return Observable.create(
      (observable: Subscriber<Order>) => {
        const user = this.loginStateService.loggedInUser;
        if (user) {
          observable.next(this._inMemoryOrders.items.find(x => (x.partnerId === user.partner_id && x.id === orderId)));
        } else {
          observable.next(null);
        }
      });
  }

  getGuestOrder(orderId: number, orderKey: string): Observable<Order> {
    return this.getOrder(orderId);
  }

  private _prepareOrderLines(orderLine: OrderLine): OrderLine[] {
    switch (orderLine.orderLineType) {
      case OrderLineType.GiftCard:
        return this._generateLines(orderLine, orderLine.quantity).map(x => { this._createGiftCard(x); return x; });
      case OrderLineType.SingleProduct:
        return this._generateLines(orderLine, orderLine.quantity);
      default:
        return [orderLine.copy()];
    }
  }

  private _generateLines(orderLine: OrderLine, count: number): OrderLine[] {
    const result = new Array<OrderLine>();

    for (let i = 0; i < count; i++) {
      const line = orderLine.copy();
      line.quantity = 1;
      result.push(line);
    }

    return result;
  }

  private _createGiftCard(orderLine: OrderLine): void {
    const giftCardInMemoryService = this.giftCardService as GiftCardInMemoryService;
    if (giftCardInMemoryService == null) {
      return;
    }

    const giftCard = giftCardInMemoryService.createGiftCard(0);
    orderLine.rfid_card_ean13 = giftCard.name;
  }

  get loggedInPartnerOrders(): Observable<OrderInfo[]> {
    return Observable.create(
      (observable: Subscriber<OrderInfo[]>) => {
        const user = this.loginStateService.loggedInUser;

        if (user) {
          observable.next(
            this._inMemoryOrders.items.filter(x => x.partnerId === user.partner_id).map(
              x => new OrderInfo({
                orderId: x.id,
                name: x.name,
                date: x.bookingDate,
                description: x.description,
                amount: x.totalAmount,
              })));
        } else {
          observable.next([]);
        }
      }
    );
  }

  private _generateOrderName(orderId: number): string {
    return `${this._orderPrefix}${(orderId + '').padStart(7, '0')}`;
  }

  orderPaymentComplete(order: Order): void {
    const foundOrder = this._inMemoryOrders.items.find(x => x.id === order.id);
    if (!foundOrder || !foundOrder.orderLines) {
      return;
    }

    foundOrder.orderLines.forEach(
      orderLine => {
        switch (orderLine.orderLineType) {
          case OrderLineType.GiftCard:
            this._changeGiftCardAmount(orderLine.rfid_card_ean13, orderLine.amount);
            break;
          default:
            break;
        }
      }
    );
  }

  private _changeGiftCardAmount(cardName: string, amount: number): void {
    const giftCardInMemoryService = this.giftCardService as GiftCardInMemoryService;
    if (!cardName || giftCardInMemoryService == null) {
      return;
    }

    giftCardInMemoryService.changeGiftCardAmount(cardName, amount);
  }

  changeOrderTimeSlot(orderId: number, date: Date, timeSlotId: number): Observable<any> {
    return of();
  }

  getOrderRestrictions(orderId: number): Observable<TimeRestriction[]> {
    return of();
  }

  cancelOrder(orderId: number): Observable<any> {
    return of();
  }

  refundOrder(orderId: number): Observable<any> {
    return of();
  }

  sendGuestOrderToEmail(orderId: number, orderKey: string, email: string): Observable<boolean> {
    return of();
  }
}
